import { useState } from 'react';
import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { fmtBR, fmtMoney } from '../lib/format';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { PageProps } from '../App';
import type { Fornecedor } from '../types';

const GRID = '1.6fr 1.1fr 1.5fr 90px 90px';

export function Fornecedores({ params }: PageProps) {
  const [sel, setSel] = useState<number | null>((params.id as number) ?? null);
  if (sel) return <Detalhe id={sel} onBack={() => setSel(null)} />;
  return <ListaForn onOpen={setSel} />;
}

function ListaForn({ onOpen }: { onOpen: (id: number) => void }) {
  const [search, setSearch] = useState('');
  const list = useFetch(() => api.listFornecedores({ search: search || undefined }), [search]);

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <div className="page-head">
        <h1>Fornecedores</h1>
        <div className="searchbox" style={{ width: 280 }}>
          <Icon name="search" size={14} className="muted" />
          <input placeholder="Buscar por nome ou CNPJ…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      <div className="tbl">
        <div className="tbl-head" style={{ gridTemplateColumns: GRID }}>
          <span>Nome</span><span>CNPJ</span><span>Categoria padrão</span><span className="cell-r">Lançtos</span><span>Origem</span>
        </div>
        {list.loading ? (
          <div className="loading">Carregando…</div>
        ) : (list.data?.items.length ?? 0) === 0 ? (
          <div className="loading">Nenhum fornecedor encontrado.</div>
        ) : (
          list.data!.items.map((f) => (
            <div key={f.id} className="tbl-row click" style={{ gridTemplateColumns: GRID }} onClick={() => onOpen(f.id)}>
              <div className="row" style={{ gap: 8 }}>
                <span className="cell-strong text-sm">{f.nome}</span>
                {!f.categoria_padrao_id && <span className="tag-new">NOVO</span>}
              </div>
              <span className="mono text-sm" style={{ color: 'var(--muted-2)' }}>{f.cnpj_cpf ?? '—'}</span>
              <span className="text-sm" style={{ color: 'var(--text-2)' }}>{f.categoria_padrao ?? '—'}</span>
              <span className="mono text-sm cell-r">{f.lancamentos_count ?? 0}</span>
              <span>{f.origem && <span className="origem">{f.origem}</span>}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Detalhe({ id, onBack }: { id: number; onBack: () => void }) {
  const toast = useToast();
  const det = useFetch(() => api.getFornecedor(id), [id]);
  const [mesclar, setMesclar] = useState(false);

  const enriquecer = async () => {
    try {
      const r = await api.enriquecerFornecedor(id);
      toast(r.ok ? 'Fornecedor enriquecido pela Receita.' : `Não foi possível enriquecer: ${r.motivo ?? '—'}`, r.ok ? 'ok' : 'err');
      det.refetch();
    } catch { toast('Falha ao enriquecer.', 'err'); }
  };

  const f = det.data;
  if (det.loading) return <div className="page detail"><div className="loading">Carregando…</div></div>;
  if (!f) return <div className="page detail"><div className="empty">Fornecedor não encontrado.</div></div>;

  const end = f.endereco;
  const campos: [string, string][] = [
    ['Razão social', f.razao_social ?? '—'],
    ['CNPJ / CPF', f.cnpj_cpf ?? '—'],
    ['CNAE', f.cnae_principal ?? '—'],
    ['Endereço', end ? [end.logradouro, end.numero, end.bairro, end.municipio && `${end.municipio}/${end.uf}`].filter(Boolean).join(', ') : '—'],
    ['Enriquecido em', fmtBR(f.enriquecido_em ?? null)],
  ];

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <a className="back-link" onClick={onBack}>← Fornecedores</a>
      <div className="page-head" style={{ margin: '10px 0 18px' }}>
        <h1>{f.nome}</h1>
        <button className="btn ghost" onClick={enriquecer}>Enriquecer (Receita)</button>
        <button className="btn ghost" onClick={() => setMesclar(true)}>Mesclar duplicata</button>
      </div>
      <div className="grid-2">
        <div className="card pad">
          <div className="card-title">Dados da Receita Federal</div>
          {campos.map(([k, v], i) => (
            <div className="kv-row" key={i}><span className="k">{k}</span><span className="v">{v}</span></div>
          ))}
          {(f.aliases?.length ?? 0) > 0 && (
            <div style={{ borderTop: '1px solid var(--border-soft)', marginTop: 12, paddingTop: 12 }}>
              <div className="k-label" style={{ marginBottom: 6 }}>Aliases reconhecidos</div>
              <div className="row wrap" style={{ gap: 6 }}>
                {f.aliases!.map((a, i) => <span key={i} className="chip-sq neutral">{a}</span>)}
              </div>
            </div>
          )}
        </div>
        <div className="card pad">
          <div className="card-title">Classificação padrão</div>
          <div className="text-sm" style={{ color: 'var(--text-2)', marginBottom: 16 }}>
            {[f.classe_padrao, f.departamento_padrao, f.categoria_padrao].filter(Boolean).join(' › ') || 'Sem padrão definido'}
          </div>
          <div className="card-title" style={{ fontSize: 13 }}>Últimos lançamentos</div>
          {(f.ultimos_lancamentos?.length ?? 0) === 0 ? (
            <div className="text-sm muted">Nenhum lançamento.</div>
          ) : (
            f.ultimos_lancamentos!.map((u) => (
              <div className="row" key={u.id} style={{ gap: 10, padding: '7px 0', borderTop: '1px solid var(--border-soft)', fontSize: 12.5 }}>
                <span className="mono muted">{fmtBR(u.vencimento)}</span>
                <span style={{ flex: 1 }} className="ellipsis">{u.descricao ?? '—'}</span>
                <span className="mono cell-strong">{fmtMoney(u.valor)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {mesclar && <MesclarModal fornAtual={f} onClose={() => setMesclar(false)} onDone={() => { setMesclar(false); toast('Fornecedores mesclados.'); onBack(); }} />}
    </div>
  );
}

function MesclarModal({ fornAtual, onClose, onDone }: { fornAtual: Fornecedor; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const list = useFetch(() => api.listFornecedores({ incluir_inativos: false }), []);
  const [destino, setDestino] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);

  const confirmar = async () => {
    if (!destino) return;
    setBusy(true);
    try { await api.mesclarFornecedor(fornAtual.id, Number(destino)); onDone(); }
    catch { toast('Falha ao mesclar.', 'err'); setBusy(false); }
  };

  return (
    <Modal title="Mesclar duplicata" onClose={onClose}
      footer={<>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={confirmar} disabled={!destino || busy}>Mesclar</button>
      </>}>
      <div className="m-sub">Os lançamentos de <b>{fornAtual.nome}</b> serão repontados para o fornecedor destino, e este será desativado.</div>
      <label className="flabel">Fornecedor destino</label>
      <select className="select" value={destino} onChange={(e) => setDestino(e.target.value ? Number(e.target.value) : '')}>
        <option value="">Escolher…</option>
        {(list.data?.items ?? []).filter((x) => x.id !== fornAtual.id).map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}
      </select>
    </Modal>
  );
}
